import clsx from 'clsx';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import LoadingSpinner from '../../../../../../common/components/LoadingSpinner';
import SHRMMessage from '../../../../../../common/components/SHRMMessage';
import { isNumeric } from '../../../../../../common/helpers';
import { useAppSelector } from '../../../../../../common/hooks';
import { IPhone } from '../../../../../../models/Account/IPhone';
import { IBasicItem } from '../../../../../../models/IBasicItem';
import { RootState } from '../../../../../../redux/store';
import { handleErrorResponse } from '../../../../../../services';
import PhoneDropdown from '../PhoneDropdown';
import styles from './PhoneModal.module.css';
import { setContactConfig } from '../../../../../../redux/accountSlice';

type PhoneModalProps = {
  phoneModalClose: () => void;
  onAddEditPhone: (newPhone: IPhone) => Promise<unknown>;
  existingPhone: IPhone;
  phones: IPhone[];
  onChangeCountry: (countryCode: string) => Promise<unknown>;
  editMode: boolean;
};

const PhoneModal = (props: PhoneModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const [popupMesage, setPopupMessage] = useState('');
  const [popupMessageColor, setPopupMessageColor] = useState('');
  const [showPopupMessage, setShowPopupMessage] = useState(false);
  const {
    register,
    handleSubmit,
    reset,
    getValues,
    formState: { errors, isValid, isDirty },
  } = useForm<IPhone>({ mode: 'all', defaultValues: props.existingPhone });

  const doesLocationExist = (location: string) => {
    const locationId = Number(location);
    if (locationId && locationId > 0)
      return props.phones?.findIndex(x => x.contactLocation === locationId) >= 0;
    else return false;
  };

  const contactConfigDataFromRedux = useAppSelector(
    (state: RootState) => state.accountReducer.contactConfigData
  );

  const [contactConfigData, setContactConfigData] = useState(contactConfigDataFromRedux);

  const contactEditData = useAppSelector(
    (state: RootState) => state.accountReducer.contactEditData
  );

  const lessThanMaxLengthPhoneNumber = (phoneNumber: string) => {
    if (!phoneNumber) return false;
    return phoneNumber.length <= contactConfigData?.phoneConfigModel.phoneNumberMaxLength;
  };

  const lessThanMaxLengthPhoneExtension = (phoneExtension: string) => {
    if (!phoneExtension) return true;
    return phoneExtension.length <= contactConfigData?.phoneConfigModel.phoneExtensionMaxLength;
  };

  const lessThanMaxLengthAreaCode = (areaCode: string) => {
    if (!areaCode) return false;
    return areaCode.length <= contactConfigData?.phoneConfigModel.phoneAreaCodeMaxLength;
  };

  const getLocationLabelClass = value => {
    let labelClass = '';
    if (props.editMode) {
      if (props.existingPhone?.contactLocation === Number(value)) {
        labelClass = styles.phoneLabel;
      }
    } else {
      if (!doesLocationExist(value)) {
        labelClass = styles.phoneLabel;
      }
    }
    return labelClass;
  };

  // const onSubmit = () => {
  //   const newPhone: IPhone = {} as IPhone;
  //   newPhone.phoneNumber = phoneNumber;

  //   setPhoneNumber('');

  //   props.onAddEditPhone(newPhone);
  //   props.phoneModalClose();
  // };
  const changeCountry = (countryCode: string) => {
    setIsLoading(true);
    props
      .onChangeCountry(countryCode)
      .then((response: any) => {
        setContactConfigData(response.data);
        const values = getValues();
        reset({
          ...values,
          phoneCountryCode: response.data.phoneConfigModel.phoneCountryCode,
          phoneCountryCodeId: response.data.phoneConfigModel.phoneCountryCodeId,
        });
      })
      .catch((_error: any) => {
        handleErrorResponse(_error);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };
  const _handleSubmit = data => {
    setIsLoading(true);
    const newPhone: IPhone = {} as IPhone;
    newPhone.phoneCountryCodeId = data.phoneCountryCodeId;
    newPhone.phoneCountryCode = data.phoneCountryCode;
    newPhone.phoneNumber = data.phoneNumber;
    newPhone.phoneExtension = data.phoneExtension;
    newPhone.phoneCode = data.phoneCode;
    newPhone.formattedNumber = '';
    // newPhone.isPrimary = false;
    newPhone.isPrimary = data.isPrimary;
    newPhone.contactEntryType = 1;
    newPhone.contactLocation = +data.contactLocation;
    props
      .onAddEditPhone(newPhone)
      .then((response: any) => {
        props.phoneModalClose();
      })
      .catch((error: any) => {
        const message = handleErrorResponse(error);
        setPopupMessage(message);
        setPopupMessageColor('var(--shrmRed)');
        setShowPopupMessage(true);
      })
      .finally(() => {
        setIsLoading(false);
        setTimeout(() => {
          setShowPopupMessage(false);
        }, 3000);
      });
  };

  useEffect(() => {
    changeCountry(props.existingPhone.phoneCountryCode ? props.existingPhone.phoneCode : 'US');
  }, []);

  return (
    <div id='modal'>
      <form className={styles.container} onSubmit={handleSubmit(_handleSubmit)}>
        <div
          className={styles.content}
          onClick={e => {
            e.stopPropagation();
          }}
        >
          <div className={styles.header}>
            <label className={styles.title}>Phone</label>
            <button className={styles.closeModalButton} onClick={props.phoneModalClose}>
              ×
            </button>
          </div>
          {isLoading ? (
            <div>
              <LoadingSpinner />
            </div>
          ) : (
            <div className={styles.form}>
              <SHRMMessage
                color={popupMessageColor}
                text={popupMesage}
                showMessage={showPopupMessage}
              />
              <div className={clsx(styles.inputLabel, errors.contactLocation && styles.error)}>
                Location: <span className={styles.required}>*</span>
              </div>
              <div className={styles.locationContainter}>
                <div className={styles.radio}>
                  {contactEditData?.contactLocations?.map((location: IBasicItem) => {
                    if (location.value !== '0') {
                      return (
                        <label
                          htmlFor={location.label}
                          key={location.value}
                          className={getLocationLabelClass(location.value)}
                        >
                          <input
                            id={location.value}
                            key={location.value}
                            name='location'
                            type='radio'
                            value={location.value}
                            {...register('contactLocation', {
                              required: true,
                            })}
                            defaultChecked={
                              props.existingPhone &&
                              props.existingPhone?.contactLocation === Number(location.value)
                            }
                            disabled={
                              typeof props.existingPhone?.contactLocation?.toString() ===
                              'undefined'
                                ? doesLocationExist(location.value)
                                : !(props.existingPhone?.contactLocation === Number(location.value))
                            }
                          />
                          {location.label}
                        </label>
                      );
                    } else return null;
                  })}
                  <p className={styles.error}>
                    {errors.contactLocation && 'This field is required'}
                  </p>
                </div>
              </div>
              <div className={styles.countryContainer}>
                <PhoneDropdown
                  label='Country / Region: '
                  propStyle={styles.dropDownProp}
                  items={contactEditData?.countries}
                  placeholderLabel={'Select country'}
                  {...register('phoneCode', {
                    onChange: e => changeCountry(e.target.value),
                  })}
                  required
                />
                <p className={styles.error}>{errors.phoneCode && 'Please enter country/region'}</p>
              </div>
              <div className={styles.inputLabel}>
                Phone: <span className={styles.required}>*</span>
              </div>
              <div className={styles.phoneNumberContainter}>
                <input
                  className={styles.phoneCountryCodeIdInputField}
                  type='hidden'
                  defaultValue={props.existingPhone?.phoneCountryCodeId || ''}
                  {...register('phoneCountryCodeId')}
                />
                <input
                  className={styles.phoneCountryCodeIdInputField}
                  type='text'
                  defaultValue={props.existingPhone?.phoneCountryCode || ''}
                  {...register('phoneCountryCode', {
                    required: true,
                    validate: { lessThanMaxLengthAreaCode },
                  })}
                />
                <input
                  className={clsx(
                    styles.phoneNumberInputField,
                    errors.phoneNumber && styles.errorField
                  )}
                  type='text'
                  defaultValue={props.existingPhone?.phoneNumber || ''}
                  // onChange={e => setPhoneNumber(e.target.value)}
                  {...register('phoneNumber', {
                    required: true,
                    validate: { lessThanMaxLengthPhoneNumber, isNumeric },
                  })}
                  maxLength={contactConfigData?.phoneConfigModel.phoneNumberMaxLength}
                />
                <div className={styles.extLabel}>ext</div>
                <input
                  className={clsx(
                    styles.phoneExtensionInputField,
                    errors.phoneExtension && styles.error
                  )}
                  type='text'
                  defaultValue={props.existingPhone?.phoneExtension || ''}
                  disabled={!contactConfigData?.phoneConfigModel.phoneExtensionEnabled}
                  {...register('phoneExtension', {
                    required: false,
                    validate: { lessThanMaxLengthPhoneExtension },
                  })}
                />
              </div>
              <div>
                <p className={styles.error}>
                  {errors.phoneCountryCodeId?.type === 'required' && 'Please enter area code'}
                </p>{' '}
                <p className={styles.error}>
                  {errors.phoneCountryCodeId?.type === 'lessThanMaxLengthAreaCode' &&
                    `Area code max length is ${contactConfigData.phoneConfigModel.phoneAreaCodeMaxLength}`}
                </p>
                <p className={styles.error}>
                  {errors.phoneNumber?.type === 'required' && 'Please enter phone number'}
                </p>{' '}
                <p className={styles.error}>
                  {errors.phoneNumber?.type === 'isNumeric' && 'Invalid Number'}
                </p>{' '}
                <p className={styles.error}>
                  {errors.phoneNumber?.type === 'lessThanMaxLengthPhoneNumber' &&
                    `Phone number max length is ${contactConfigData.phoneConfigModel.phoneNumberMaxLength}`}
                </p>
                <p className={styles.error}>
                  {errors.phoneExtension?.type === 'lessThanMaxLengthPhoneExtension' &&
                    `Extension max length is ${contactConfigData.phoneConfigModel.phoneExtensionMaxLength}`}
                </p>
              </div>
            </div>
          )}
          <div className={styles.footer}>
            {/* <input className={styles.saveButton} value='Save' onClick={onSubmit} /> */}
            <button type='submit' className={styles.saveButton} disabled={!(isDirty && isValid)}>
              Save
            </button>
            <div className={styles.cancelButton} onClick={props.phoneModalClose}>
              Cancel
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default PhoneModal;
